import React from 'react'
import PropTypes from 'prop-types'
import { DataTable } from '../../../group_leader/components/common'
import { makeT } from '../../../group_leader/helpers/i18n'
import { PLACEHOLDER } from '../../../group_leader/helpers/value'
import { cellValue, entryAffiliation, entryName, matchPlayColumns, multiMatchColumns } from '../../helpers/columns'
import { extractMatches, extractMultiMatchEntries } from '../../helpers/results'
import PositionBadge from './PositionBadge'

const t = makeT('group_leader.event_results.leaderboard')

// "Player v. all other players": one row per golfer with their match record.
const MultiMatchTable = ({ leaderboard, name }) => {
  const entries = extractMultiMatchEntries(leaderboard)

  const renderCell = (entry, column) => {
    if (column.key === 'position') {
      return <PositionBadge entry={entry}/>
    }
    if (column.key === 'name') {
      return (
        <>
          <span className="gl-event-results-player-name">{entryName(entry)}</span>
          {affiliationLine(entry)}
        </>
      )
    }
    return <span className="gl-event-results-score-val">{cellValue(entry, column.key)}</span>
  }

  return (
    <DataTable
      columns={multiMatchColumns(leaderboard)}
      rows={entries}
      rowKey={(entry, index) => entry.id_str || entry.id || index}
      renderCell={renderCell}
      ariaLabel={t('match_play_aria', { name })}
      renderMobileRow={(entry) => (
        <div className="gl-event-results-lb-mobile-row">
          <PositionBadge entry={entry}/>
          <div className="gl-event-results-lb-mobile-name">
            {entryName(entry)}
            {affiliationLine(entry)}
          </div>
          <div className="gl-event-results-lb-mobile-score">{cellValue(entry, 'score')}</div>
        </div>
      )}
      />
  )
}

MultiMatchTable.propTypes = {
  leaderboard: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
}

// A scope is one match: both sides in scope order, the winner in bold, as the legacy view does.
const affiliationLine = (entry) => {
  const affiliation = entry ? entryAffiliation(entry) : ''
  if (!affiliation) { return null }

  return <span className="gl-event-results-player-affiliation">{affiliation}</span>
}

const sideName = (side, isWinner) => (
  <span
    className={isWinner
      ? 'gl-event-results-player-name gl-event-results-player-name--won'
      : 'gl-event-results-player-name'}
    >
    {side ? entryName(side) : PLACEHOLDER}
  </span>
)

const resultText = (match) => {
  if (match.tbd) { return PLACEHOLDER }
  if (match.result) { return match.result }
  return match.winner === null ? t('halved') : PLACEHOLDER
}

const HeadToHeadTable = ({ leaderboard, name }) => {
  const matches = extractMatches(leaderboard)

  const renderCell = (match, column) => {
    switch (column.key) {
      case 'name':
        return <>{sideName(match.first, match.winner === match.first)}{affiliationLine(match.first)}</>
      case 'opponent':
        return <>{sideName(match.second, match.winner === match.second)}{affiliationLine(match.second)}</>
      case 'thru':
        return <span className="gl-event-results-score-val">{cellValue(match.first, 'thru')}</span>
      default:
        return <span className="gl-event-results-score-val">{resultText(match)}</span>
    }
  }

  return (
    <DataTable
      columns={matchPlayColumns(leaderboard)}
      rows={matches}
      rowKey={({ first }, index) => first?.id_str || first?.id || index}
      renderCell={renderCell}
      ariaLabel={t('match_play_aria', { name })}
      renderMobileRow={(match) => (
        <div className="gl-event-results-lb-mobile-row">
          <div className="gl-event-results-lb-mobile-name">
            {sideName(match.first, match.winner === match.first)}
            {match.second && t('versus', { opponent: entryName(match.second) })}
          </div>
          <div className="gl-event-results-lb-mobile-score">{resultText(match)}</div>
        </div>
      )}
      />
  )
}

HeadToHeadTable.propTypes = {
  leaderboard: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
}

const MatchPlayLeaderboard = ({ leaderboard, name, isMulti }) => (
  isMulti
    ? <MultiMatchTable leaderboard={leaderboard} name={name}/>
    : <HeadToHeadTable leaderboard={leaderboard} name={name}/>
)

MatchPlayLeaderboard.propTypes = {
  leaderboard: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  isMulti: PropTypes.bool,
}

export default MatchPlayLeaderboard
